import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  buildShareUrl,
  captureViewQuery,
  parseSvName,
  pickParamsByConfig,
  queriesMatch,
} from './query';
import { getSavedViews, setSavedViews } from './storage';
import type { SavedView, SavedViewsPageConfig } from './types';
import { MAX_SAVED_VIEWS_PER_PAGE, SHARED_VIEW_NAME_PARAM } from './types';

interface UseSavedViewsOptions {
  config: SavedViewsPageConfig;
  organizationId: string;
}

const makeId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `sv_${Date.now()}_${Math.round(Math.random() * 1e9)}`;

/**
 * Binds saved-views storage + the page's URL search params to the UI.
 * All serialization lives in the pure `query`/`storage` helpers — this hook is
 * just React glue (state owner, param writer).
 */
export const useSavedViews = ({ config, organizationId }: UseSavedViewsOptions) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { pageKey } = config;
  const [views, setViews] = useState<SavedView[]>(() =>
    organizationId ? getSavedViews(pageKey, organizationId) : [],
  );

  // Re-hydrate when the org (or page) changes in place. The tab stays mounted
  // across an org switch (see the sibling chart-visibility effect), so without
  // this the list — and any subsequent save — would target the previous org.
  useEffect(() => {
    setViews(organizationId ? getSavedViews(pageKey, organizationId) : []);
  }, [pageKey, organizationId]);

  // Functional update so overlapping callbacks (e.g. a rename's onBlur firing as
  // another row's delete onClick runs) never operate on a stale `views` closure.
  const persist = useCallback(
    (update: (prev: SavedView[]) => SavedView[]) => {
      setViews(prev => {
        const next = update(prev);
        if (organizationId) setSavedViews(pageKey, organizationId, next);
        return next;
      });
    },
    [pageKey, organizationId],
  );

  const isFull = views.length >= MAX_SAVED_VIEWS_PER_PAGE;

  const saveCurrentView = useCallback(
    (name: string): boolean => {
      const trimmed = name.trim();
      if (!trimmed || views.length >= MAX_SAVED_VIEWS_PER_PAGE) return false;
      const view: SavedView = {
        id: makeId(),
        name: trimmed,
        query: captureViewQuery(searchParams, config),
        createdAt: Date.now(),
      };
      persist(prev => [...prev, view]);
      return true;
    },
    [config, persist, searchParams, views.length],
  );

  const renameView = useCallback(
    (id: string, name: string) => {
      const trimmed = name.trim();
      if (!trimmed) return;
      persist(prev => prev.map(v => (v.id === id ? { ...v, name: trimmed } : v)));
    },
    [persist],
  );

  const deleteView = useCallback(
    (id: string) => persist(prev => prev.filter(v => v.id !== id)),
    [persist],
  );

  const applyView = useCallback(
    (view: SavedView) => {
      // Replace the whole query with the view's params so stale filters don't
      // linger. `page` is not in any whitelist, so it drops here too — matching
      // the page's own filter-change reset.
      setSearchParams(new URLSearchParams(view.query), { replace: false });
    },
    [setSearchParams],
  );

  const shareUrlFor = useCallback((view: SavedView) => {
    if (typeof window === 'undefined') return '';
    // Use the live pathname so the org-scoped route prefix (e.g. /org-123/qc)
    // is preserved — the config basePath ('/qc') would drop it.
    return buildShareUrl(view, window.location.origin, window.location.pathname);
  }, []);

  /**
   * The view whose saved filters match the page's current filters, if any —
   * derived, not tracked, so it survives reload and lights up after a shared
   * link loads. First match wins (single selection).
   */
  const activeViewId = useMemo(() => {
    const currentPicked = pickParamsByConfig(searchParams, config);
    return views.find(v => queriesMatch(currentPicked, v.query))?.id ?? null;
  }, [config, searchParams, views]);

  /** Suggested name from a shared link, to pre-fill the save dialog. */
  const pendingName = useMemo(() => parseSvName(searchParams), [searchParams]);

  /** Drop `sv_name` from the URL once consumed, so it doesn't re-prefill later saves. */
  const clearPendingName = useCallback(() => {
    if (!searchParams.has(SHARED_VIEW_NAME_PARAM)) return;
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.delete(SHARED_VIEW_NAME_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  return {
    views,
    activeViewId,
    isFull,
    saveCurrentView,
    renameView,
    deleteView,
    applyView,
    shareUrlFor,
    pendingName,
    clearPendingName,
  };
};
